import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useMasters } from '../../hooks/useMasters';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../store/auth';
import { useLatestRates } from '../../hooks/useLatestRates';
import { date, fine, money, testingWords, today, weight } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Stat, Table } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DateInput, Drawer, Field, NumberInput, PercentInput, Select } from '../../components/ui/Form';

/* ═══════════════════════════ shapes ══════════════════════════════════════ */

interface MeltRow {
  id: string;
  doc_no: string;
  melt_date: string;
  received_date: string | null;
  status: string;
  branch_name: string;
  metal_name: string;
  melter: string;
  melter_type: string;
  input_fine_wt: string;
  input_cost: string;
  output_wt: string | null;
  output_fine_wt: string | null;
  sample_fine_wt: string;
  recovered_fine_wt: string;
  loss_fine_wt: string | null;
  loss_pct: string | null;
  allowed_loss_pct: string;
  excess_loss_value: string;
  charges: string;
  charges_tax: string;
  bar_count: number;
  input_count: string;
  bar_nos: string | null;
  days_out: number;
  expected_return_date: string | null;
  days_overdue: number;
  charges_fine_wt: string;
  excess_loss_treatment: 'WRITE_OFF' | 'RECOVER';
  recovered_amount: string;
  refiner_id: string | null;
  returned_note: string | null;
}

interface MeltDetail extends MeltRow {
  metal_id: string;
  branch_id: string;
  refiner_id: string | null;
  refiner_name: string | null;
  refiner_registered: boolean | null;
  melter_name: string | null;
  input_gross_wt: string;
  sample_wt: string;
  recovered_wt: string;
  recovered_purity_code: string | null;
  recovered_lot_code: string | null;
  excess_loss_fine_wt: string;
  charges_paid_by: string | null;
  charges_interstate: boolean;
  refiner_bill_no: string | null;
  testing_method: string | null;
  assay_cert_no: string | null;
  assayed_by: string | null;
  remarks: string | null;
  requested_by: string;
  requested_by_name: string | null;
  received_by: string | null;
  received_by_name: string | null;
  approved_by_name: string | null;
  cancelled_by_name: string | null;
  cancel_reason: string | null;
  issue_journal_no: string | null;
  completion_journal_no: string | null;
  issue_journal_id: string | null;
  issued_at: string | null;
  recovery_rate: string | null;
  lines: {
    line_no: number;
    description: string | null;
    gross_wt: string;
    net_wt: string;
    fine_wt: string;
    cost_amount: string;
    purity_code: string;
    item_code: string | null;
    lot_code: string | null;
    product_name: string | null;
    item_status: string | null;
  }[];
  bars: {
    line_no: number;
    bar_no: string;
    weight: string;
    fine_wt: string;
    assay_pct: string | null;
    hallmark_uid: string | null;
    cost_amount: string | null;
    stock_item_id: string | null;
    purity_code: string;
    item_status: string | null;
    sold_doc_no: string | null;
    sold_doc_id: string | null;
  }[];
  journals: {
    id: string;
    journal_no: string;
    journal_date: string;
    narration: string | null;
    status: string;
    debit: string;
    credit: string;
    account_code: string;
    account_name: string;
  }[];
  trail: { occurred_at: string; action: string; reason: string | null; user_name: string | null }[];
}

interface MeltableItem {
  id: string;
  item_code: string;
  product_name: string;
  purity_code: string;
  gross_wt: string;
  net_wt: string;
  fine_wt: string;
  acquisition_cost: string;
  acquisition_doc_type: string | null;
}

interface MeltableLot {
  id: string;
  lot_code: string;
  product_name: string;
  purity_code: string;
  remaining_wt: string;
  remaining_fine_wt: string;
  cost_per_fine_gram: string;
  source_doc_type: string | null;
}

interface Bar {
  bar_no: string;
  weight: string;
  fine_wt: string;
  assay_pct: string | null;
  hallmark_uid: string | null;
  cost_amount: string | null;
  cost_per_fine_gram: string | null;
  purity_code: string;
  fineness: string;
  metal_id: string;
  metal_name: string;
  melting_id: string;
  doc_no: string;
  received_date: string;
  days_held: number;
  branch_id: string;
  branch_name: string;
  stock_item_id: string | null;
  status: string | null;
  sold_doc_no: string | null;
  sold_doc_id: string | null;
}

interface Supplier {
  id: string;
  name: string;
  supplier_type?: string;
  is_registered?: boolean;
  is_active?: boolean;
}

/* ═══════════════════════════ small pieces ════════════════════════════════ */

const STATUS: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'mute' }> = {
  IN_PROCESS: { label: 'Out for melting', tone: 'warn' },
  PENDING_APPROVAL: { label: 'Awaiting approval', tone: 'bad' },
  POSTED: { label: 'Completed', tone: 'good' },
  CANCELLED: { label: 'Cancelled', tone: 'mute' },
  DRAFT: { label: 'Draft', tone: 'mute' },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status.toLowerCase(), tone: 'mute' as const };
  return <Chip tone={s.tone}>{s.label}</Chip>;
}

const ACTION_LABEL: Record<string, string> = {
  MELTING_ISSUED: 'Sent for melting',
  MELTING_RECEIVED: 'Bars received',
  MELTING_SENT_FOR_APPROVAL: 'Held for loss approval',
  MELTING_APPROVAL_NOTE: 'Approver’s note',
  MELTING_APPROVED_AND_POSTED: 'Loss approved — posted',
  MELTING_POSTED: 'Posted',
  MELTING_SENT_BACK: 'Sent back to re-enter',
  MELTING_CANCELLED: 'Cancelled',
  MELTING_REOPENED: 'Reopened',
  MELTING_RETURN_DATE_CHANGED: 'Return-by date changed',
};

const TESTING = [
  { value: 'FIRE_ASSAY', label: 'Fire assay' },
  { value: 'XRF', label: 'XRF' },
  { value: 'TOUCHSTONE', label: 'Touchstone' },
  { value: 'CARAT_METER', label: 'Carat meter' },
];

const n = (v: string | null | undefined): number => Number(v || 0);

/**
 * A loss percentage beside its allowance. When the two print the same to two
 * places but the loss is still over (1.5006% against 1.50%), a third place is
 * shown, so "1.50% is over 1.50%" never appears.
 */
const pct = (value: number, allowed?: number): string =>
  allowed !== undefined && value > allowed && value.toFixed(2) === allowed.toFixed(2)
    ? `${value.toFixed(3)}%`
    : `${value.toFixed(2)}%`;

function Row({
  label,
  children,
  strong,
  tone,
}: {
  label: string;
  children: ReactNode;
  strong?: boolean;
  tone?: string;
}) {
  return (
    <div className={`flex justify-between gap-3 py-0.5 ${strong ? 'border-t border-ink/10 pt-1 font-semibold' : ''}`}>
      <span className="text-ink-soft">{label}</span>
      <span className={`num ${tone ?? ''}`}>{children}</span>
    </div>
  );
}

/** A reason box inside a confirm dialog, for the actions that need one. */
function ReasonDialog({
  open,
  title,
  message,
  label,
  placeholder,
  confirmLabel,
  tone,
  busy,
  required = true,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  label: string;
  placeholder: string;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  required?: boolean;
  onCancel: () => void;
  onConfirm: (text: string) => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!open) setText('');
  }, [open]);
  return (
    <ConfirmDialog
      open={open}
      title={title}
      message={message}
      tone={tone ?? 'primary'}
      detail={
        <label className="block text-sm">
          <span className="mb-1 block text-2xs text-ink-soft">{label}</span>
          <input
            className="field w-full"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus
          />
        </label>
      }
      confirmLabel={confirmLabel}
      confirmDisabled={required && text.trim().length < 3}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(text.trim())}
    />
  );
}

/* ═══════════════════════════ the page ════════════════════════════════════ */

type Tab = 'IN_PROCESS' | 'PENDING_APPROVAL' | 'POSTED' | 'CANCELLED' | 'BARS';

/**
 * Melting, as it happens in a shop: the metal goes out one day, the bars come
 * back days later.
 *
 * While it is out the metal sits in "Metal at melting / refinery", at cost, so
 * it never vanishes from the books. When the bars come back they are entered
 * with the assay, the sample cut for it, any dust recovered and the refiner's
 * bill. Loss within the allowance posts at once; above it, a second person
 * signs it off. Every bar becomes a tagged piece that can be sold at the
 * counter.
 */
export function MeltingPage() {
  const { can } = usePermission();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'IN_PROCESS';
  const setTab = (t: Tab): void => setParams({ tab: t }, { replace: true });
  const openId = params.get('open');
  const setOpenId = (id: string | null): void => {
    const next = new URLSearchParams(params);
    if (id) next.set('open', id);
    else next.delete('open');
    setParams(next, { replace: true });
  };

  // "Send for melting" from another screen (the old-metal lots) arrives as
  // ?send=<metal id>, and opens the form on that metal.
  const sendMetal = params.get('send');
  const [issuing, setIssuing] = useState(sendMetal !== null);
  const [remelt, setRemelt] = useState<Bar | null>(null);
  const [receiving, setReceiving] = useState<MeltDetail | null>(null);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const value = useBarValue();

  const list = useQuery({
    queryKey: ['meltings'],
    queryFn: () => api.get<{ data: MeltRow[] }>('/meltings?limit=500'),
  });
  const bars = useQuery({
    queryKey: ['melting-bars'],
    queryFn: () => api.get<{ data: Bar[] }>('/meltings/bars'),
  });

  const rows = list.data?.data ?? [];
  const byStatus = (s: string): MeltRow[] => rows.filter((r) => r.status === s);
  const out = byStatus('IN_PROCESS');
  const pending = byStatus('PENDING_APPROVAL');
  const done = byStatus('POSTED');
  const barsOnHand = bars.data?.data ?? [];

  const yearStart = (() => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-04-01`;
  })();
  const thisYear = done.filter((r) => (r.received_date ?? r.melt_date) >= yearStart);
  const late = out.filter((r) => r.days_overdue > 0);

  // Grams of gold and grams of silver are never added together: each figure
  // is given metal by metal.
  const perMetal = <T,>(list: T[], metal: (x: T) => string, amount: (x: T) => number): string =>
    [...list.reduce((m, x) => m.set(metal(x), (m.get(metal(x)) ?? 0) + amount(x)), new Map<string, number>())]
      .map(([k, v]) => `${k} ${fine(v.toFixed(4))}`)
      .join(' · ') || '—';
  const yearLoss = [
    ...thisYear.reduce((m, r) => {
      const x = m.get(r.metal_name) ?? { in: 0, loss: 0 };
      return m.set(r.metal_name, { in: x.in + n(r.input_fine_wt), loss: x.loss + n(r.loss_fine_wt) });
    }, new Map<string, { in: number; loss: number }>()),
  ];
  const barsValue = barsOnHand.reduce((a, b) => a + (value(b) ?? 0), 0);
  const barsCost = barsOnHand.reduce((a, b) => a + n(b.cost_amount), 0);

  // The search and dates narrow whichever list is showing.
  const q = search.trim().toLowerCase();
  const matches = (r: MeltRow): boolean =>
    (!q || `${r.doc_no} ${r.melter} ${r.metal_name} ${r.bar_nos ?? ''} ${r.branch_name}`.toLowerCase().includes(q)) &&
    (!from || r.melt_date.slice(0, 10) >= from) &&
    (!to || r.melt_date.slice(0, 10) <= to);
  const barMatches = (b: Bar): boolean =>
    (!q ||
      `${b.bar_no} ${b.doc_no} ${b.metal_name} ${b.purity_code} ${b.hallmark_uid ?? ''}`.toLowerCase().includes(q)) &&
    (!from || b.received_date.slice(0, 10) >= from) &&
    (!to || b.received_date.slice(0, 10) <= to);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'IN_PROCESS', label: 'Out for melting', count: out.length },
    { key: 'PENDING_APPROVAL', label: 'Awaiting approval', count: pending.length },
    { key: 'POSTED', label: 'Completed', count: done.length },
    { key: 'BARS', label: 'Bars on hand', count: barsOnHand.length },
    { key: 'CANCELLED', label: 'Cancelled', count: byStatus('CANCELLED').length },
  ];

  const shown = tab === 'BARS' ? [] : byStatus(tab).filter(matches);
  const shownBars = barsOnHand.filter(barMatches);

  return (
    <>
      <PageHeader
        title="Melting and refining"
        subtitle="Metal out, bars back — every gram and every rupee accounted for while it is away."
        actions={
          <div className="flex gap-2">
            <Link className="btn-ghost" to="/melting/melters">
              Melters and refiners
            </Link>
            <Link className="btn-ghost" to="/reports?group=Melting">
              Reports
            </Link>
            {can('melting.create') ? (
              <button className="btn-primary" onClick={() => setIssuing(true)}>
                Send for melting
              </button>
            ) : null}
          </div>
        }
      />
      <ErrorNotice error={list.error} />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Out for melting"
          value={`${out.length} melt${out.length === 1 ? '' : 's'}`}
          hint={`${perMetal(
            out,
            (r) => r.metal_name,
            (r) => n(r.input_fine_wt),
          )} · ${money(out.reduce((a, r) => a + n(r.input_cost), 0).toFixed(2))} at cost`}
        />
        <Stat
          label="Past the due date"
          value={String(late.length)}
          hint={
            late.length > 0
              ? `Oldest ${Math.max(...late.map((r) => r.days_overdue))} days late`
              : 'Everything is on time'
          }
          {...(late.length > 0 ? { tone: 'bad' as const } : {})}
        />
        <Stat
          label="Bars on hand"
          value={barsValue > 0 ? money(barsValue.toFixed(2)) : money(barsCost.toFixed(2))}
          hint={`${barsOnHand.length} bar${barsOnHand.length === 1 ? '' : 's'} · ${perMetal(
            barsOnHand,
            (b) => b.metal_name,
            (b) => n(b.fine_wt),
          )}${barsValue > 0 ? ` · cost ${money(barsCost.toFixed(2))}` : ''}`}
          {...(barsValue > 0 && barsValue < barsCost ? { tone: 'warn' as const } : {})}
        />
        <Stat
          label="Loss this year"
          value={
            yearLoss.length === 1 && yearLoss[0]![1].in > 0
              ? `${((yearLoss[0]![1].loss / yearLoss[0]![1].in) * 100).toFixed(2)}%`
              : yearLoss.length > 1
                ? `${yearLoss.length} metals`
                : '—'
          }
          hint={
            yearLoss
              .map(
                ([metal, x]) =>
                  `${metal} ${x.in > 0 ? ((x.loss / x.in) * 100).toFixed(2) : '0.00'}% of ${fine(x.in.toFixed(4))}`,
              )
              .join(' · ') || 'Nothing melted yet'
          }
        />
      </div>
      {pending.length > 0 && tab !== 'PENDING_APPROVAL' ? (
        <button
          className="mb-4 flex w-full items-center justify-between rounded-md border border-danger/30 bg-danger/5 px-4 py-2 text-left text-sm"
          onClick={() => setTab('PENDING_APPROVAL')}
        >
          <span>
            <span className="font-semibold">{pending.length}</span> melt{pending.length === 1 ? '' : 's'} waiting for
            someone to approve the loss
          </span>
          <span className="text-gold-700">Review →</span>
        </button>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-1 border-b border-silver-100" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === t.key
                ? 'border-gold-600 font-semibold text-ink'
                : 'border-transparent text-ink-soft hover:text-ink'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="num ml-1.5 rounded bg-silver-100 px-1.5 text-2xs">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <input
          className="field w-72"
          placeholder={tab === 'BARS' ? 'Find a bar, melt, purity or HUID' : 'Find a melt, melter, bar or branch'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex items-end gap-2">
          <label className="text-2xs text-ink-soft">
            From
            <DateInput value={from} onChange={setFrom} />
          </label>
          <label className="text-2xs text-ink-soft">
            To
            <DateInput value={to} onChange={setTo} />
          </label>
          {search || from || to ? (
            <button
              className="btn-quiet text-xs"
              onClick={() => {
                setSearch('');
                setFrom('');
                setTo('');
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
        {tab !== 'BARS' && shown.length > 0 ? (
          <span className="ml-auto text-2xs text-ink-soft">
            {shown.length} melt{shown.length === 1 ? '' : 's'} ·{' '}
            {perMetal(
              shown,
              (r) => r.metal_name,
              (r) => n(r.input_fine_wt),
            )}{' '}
            sent
          </span>
        ) : null}
      </div>

      {list.isLoading ? <Loading /> : null}

      {tab === 'BARS' ? (
        <BarsTable
          bars={shownBars}
          loading={bars.isLoading}
          onOpen={setOpenId}
          onRemelt={(b) => setRemelt(b)}
          value={value}
        />
      ) : !list.isLoading && shown.length === 0 && (q || from || to) ? (
        <Empty message="Nothing matches." hint="Clear the search or the dates to see everything." />
      ) : !list.isLoading && shown.length === 0 ? (
        <Empty
          message={
            tab === 'IN_PROCESS'
              ? 'Nothing is out for melting.'
              : tab === 'PENDING_APPROVAL'
                ? 'No melt is waiting for approval.'
                : tab === 'POSTED'
                  ? 'No melts completed yet.'
                  : 'Nothing cancelled.'
          }
          hint="Send old gold, scrap or unsold pieces for melting; enter the bars when they come back."
          action={
            can('melting.create') && tab === 'IN_PROCESS'
              ? { label: 'Send for melting', onClick: () => setIssuing(true) }
              : undefined
          }
        />
      ) : shown.length > 0 ? (
        <MeltTable rows={shown} tab={tab} onOpen={setOpenId} />
      ) : null}

      {issuing ? (
        <IssueDrawer
          initialMetalId={sendMetal ?? ''}
          onClose={() => {
            setIssuing(false);
            if (sendMetal !== null) setTab(tab);
          }}
          onIssued={(id) => {
            setIssuing(false);
            setTab('IN_PROCESS');
            setOpenId(id);
          }}
        />
      ) : null}

      {remelt ? (
        <IssueDrawer
          initialMetalId={remelt.metal_id}
          initialBranchId={remelt.branch_id}
          initialItemIds={remelt.stock_item_id ? [remelt.stock_item_id] : []}
          onClose={() => setRemelt(null)}
          onIssued={(id) => {
            setRemelt(null);
            setTab('IN_PROCESS');
            setOpenId(id);
          }}
        />
      ) : null}

      {openId && !receiving ? (
        <DetailDrawer id={openId} onClose={() => setOpenId(null)} onReceive={(m) => setReceiving(m)} />
      ) : null}

      {receiving ? (
        <ReceiveDrawer melt={receiving} onClose={() => setReceiving(null)} onDone={() => setReceiving(null)} />
      ) : null}
    </>
  );
}

/* ═══════════════════════════ lists ═══════════════════════════════════════ */

function MeltTable({ rows, tab, onOpen }: { rows: MeltRow[]; tab: Tab; onOpen: (id: string) => void }) {
  if (tab === 'IN_PROCESS') {
    return (
      <Table
        head={[
          'Sent',
          'Days out',
          { label: 'Due back', align: 'left' },
          'Melt',
          'With',
          'Metal',
          'Lines',
          'Fine',
          'Cost',
          '',
        ]}
      >
        {rows.map((m) => (
          <tr
            key={m.id}
            className={`cursor-pointer hover:bg-gold-50 ${m.days_overdue > 0 ? 'bg-danger/5' : ''}`}
            onClick={() => onOpen(m.id)}
          >
            <td className="td">{date(m.melt_date)}</td>
            <td className="td num text-right">{m.days_out}</td>
            <td className="td whitespace-nowrap">
              {m.expected_return_date ? date(m.expected_return_date) : '—'}
              {m.days_overdue > 0 ? (
                <span className="ml-2">
                  <Chip tone="bad">{m.days_overdue}d late</Chip>
                </span>
              ) : null}
            </td>
            <td className="td num">{m.doc_no}</td>
            <td className="td">{m.melter}</td>
            <td className="td">{m.metal_name}</td>
            <td className="td num text-right">{m.input_count}</td>
            <td className="td num text-right">{fine(m.input_fine_wt)}</td>
            <td className="td num text-right">{money(m.input_cost)}</td>
            <td className="td text-right text-xs">
              {m.returned_note ? (
                <span className="text-danger">Sent back</span>
              ) : (
                <span className="text-gold-700">Receive →</span>
              )}
            </td>
          </tr>
        ))}
      </Table>
    );
  }
  return (
    <Table
      head={[
        'Sent',
        'Back',
        'Melt',
        'With',
        'Fine in',
        'Fine in bars',
        { label: 'Loss', align: 'right' },
        'Loss %',
        { label: 'Charges', align: 'right' },
        { label: 'Bars', align: 'left' },
        'Status',
      ]}
    >
      {rows.map((m) => {
        const over = n(m.loss_pct) > n(m.allowed_loss_pct);
        return (
          <tr key={m.id} className="cursor-pointer hover:bg-gold-50" onClick={() => onOpen(m.id)}>
            <td className="td">{date(m.melt_date)}</td>
            <td className="td">{m.received_date ? date(m.received_date) : '—'}</td>
            <td className="td num">{m.doc_no}</td>
            <td className="td">{m.melter}</td>
            <td className="td num text-right">{fine(m.input_fine_wt)}</td>
            <td className="td num text-right">{m.output_fine_wt ? fine(m.output_fine_wt) : '—'}</td>
            <td className="td num text-right">{m.loss_fine_wt ? fine(m.loss_fine_wt) : '—'}</td>
            <td className={`td num text-right ${over ? 'font-semibold text-danger' : ''}`}>
              {m.loss_pct ? pct(n(m.loss_pct), n(m.allowed_loss_pct)) : '—'}
            </td>
            <td className="td num text-right">
              {n(m.charges) > 0 ? money(m.charges) : '—'}
              {n(m.charges_fine_wt) > 0 ? (
                <span className="block text-2xs text-ink-soft">+ {fine(m.charges_fine_wt)} in metal</span>
              ) : null}
              {n(m.recovered_amount) > 0 ? (
                <span className="block text-2xs text-success">{money(m.recovered_amount)} charged back</span>
              ) : null}
            </td>
            <td className="td num text-xs">{m.bar_nos ?? '—'}</td>
            <td className="td">
              <StatusChip status={m.status} />
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

/**
 * What a bar is worth at today's board rate: its weight at the rate for its
 * own purity when one is set, otherwise its fine weight at the metal's rate
 * per fine gram. No rate set for the metal, no value — never a guess.
 */
function useBarValue(): (b: Bar) => number | null {
  const { rates } = useLatestRates();
  const masters = useMasters();
  return (b: Bar): number | null => {
    const mine = rates.filter((r) => r.metalId === b.metal_id);
    if (mine.length === 0) return null;
    const same = mine.find((r) => r.label === b.purity_code && !r.perFine);
    if (same) return n(same.value) * n(b.weight);
    // Per fine gram, from the purest rate published.
    const perFine = mine
      .map((r) => {
        if (r.perFine) return n(r.value);
        const pu = masters.purities.find((p) => p.code === r.label && p.metal_id === r.metalId);
        return pu && n(pu.fineness) > 0 ? n(r.value) / n(pu.fineness) : 0;
      })
      .filter((v) => v > 0);
    return perFine.length ? Math.max(...perFine) * n(b.fine_wt) : null;
  };
}

function BarsTable({
  bars,
  loading,
  onOpen,
  onRemelt,
  value,
}: {
  bars: Bar[];
  loading: boolean;
  onOpen: (id: string) => void;
  onRemelt: (b: Bar) => void;
  value: (b: Bar) => number | null;
}) {
  const { can } = usePermission();
  const navigate = useNavigate();
  if (loading) return <Loading />;
  if (bars.length === 0) {
    return <Empty message="No bars on hand." hint="Bars appear here once a melt is received and posted." />;
  }
  const totalCost = bars.reduce((a, b) => a + n(b.cost_amount), 0);
  const totalValue = bars.reduce((a, b) => a + (value(b) ?? 0), 0);
  return (
    <>
      <Table
        head={[
          'Cast',
          'Bar',
          'Melt',
          'Metal',
          'Purity',
          'Assay %',
          'HUID',
          'Weight',
          'Fine',
          'Cost',
          { label: 'Today’s value', align: 'right' },
          { label: 'Gain', align: 'right' },
          '',
        ]}
      >
        {bars.map((b) => {
          const v = value(b);
          const gain = v === null ? null : v - n(b.cost_amount);
          return (
            <tr key={b.bar_no}>
              <td className="td whitespace-nowrap">
                {date(b.received_date)}
                <span className="block text-2xs text-ink-soft">{b.days_held} days held</span>
              </td>
              <td className="td num font-medium">{b.bar_no}</td>
              <td className="td num">
                <button
                  className="text-gold-700 underline-offset-2 hover:underline"
                  onClick={() => onOpen(b.melting_id)}
                >
                  {b.doc_no}
                </button>
              </td>
              <td className="td">{b.metal_name}</td>
              <td className="td num">{b.purity_code}</td>
              <td className="td num text-right">{b.assay_pct ? n(b.assay_pct).toFixed(2) : '—'}</td>
              <td className="td num">{b.hallmark_uid ?? '—'}</td>
              <td className="td num text-right">{weight(b.weight)}</td>
              <td className="td num text-right">{fine(b.fine_wt)}</td>
              <td className="td num text-right">
                {money(b.cost_amount)}
                <span className="block text-2xs text-ink-soft">{money(b.cost_per_fine_gram)}/g</span>
              </td>
              <td className="td num text-right">
                {v === null ? <span className="text-2xs text-ink-soft">no rate set</span> : money(v.toFixed(2))}
              </td>
              <td className={`td num text-right ${gain === null ? '' : gain >= 0 ? 'text-success' : 'text-danger'}`}>
                {gain === null ? '—' : `${gain >= 0 ? '+' : '−'}${money(Math.abs(gain).toFixed(2))}`}
              </td>
              <td className="td whitespace-nowrap text-right">
                <button
                  className="btn-quiet px-2 py-1 text-xs"
                  onClick={() => navigate(`/melting/${b.melting_id}/tags`)}
                >
                  Tag
                </button>
                {can('melting.create') && b.status === 'IN_STOCK' ? (
                  <button className="btn-quiet px-2 py-1 text-xs" onClick={() => onRemelt(b)}>
                    Re-melt
                  </button>
                ) : null}
                {can('sales.create') && b.status === 'IN_STOCK' ? (
                  <button
                    className="btn-ghost px-2 py-1 text-xs"
                    onClick={() => navigate(`/pos?item=${encodeURIComponent(b.bar_no)}`)}
                  >
                    Sell
                  </button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </Table>
      <p className="mt-2 text-right text-sm">
        <span className="text-ink-soft">{bars.length} bars · cost </span>
        <span className="num font-medium">{money(totalCost.toFixed(2))}</span>
        {totalValue > 0 ? (
          <>
            <span className="text-ink-soft"> · worth today </span>
            <span className="num font-medium">{money(totalValue.toFixed(2))}</span>
            <span className={`num ml-2 font-semibold ${totalValue >= totalCost ? 'text-success' : 'text-danger'}`}>
              {totalValue >= totalCost ? '+' : '−'}
              {money(Math.abs(totalValue - totalCost).toFixed(2))}
            </span>
          </>
        ) : null}
      </p>
    </>
  );
}

/* ═══════════════════════════ send for melting ════════════════════════════ */

/** Days a melt is usually out: matches the server's default. */
const USUAL_DAYS_OUT = { IN_HOUSE: 2, REFINER: 7 } as const;
const plusDays = (day: string, days: number): string => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function IssueDrawer({
  initialMetalId,
  initialItemIds = [],
  initialBranchId = '',
  onClose,
  onIssued,
}: {
  initialMetalId: string;
  initialBranchId?: string;
  /** Pieces already chosen — a bar being sent back for re-melting. */
  initialItemIds?: string[];
  onClose: () => void;
  onIssued: (id: string) => void;
}) {
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const masters = useMasters();
  const { branches, defaultBranchId } = useBranches();
  const suppliers = useQuery({ queryKey: ['suppliers', ''], queryFn: () => endpoints.parties.suppliers() });
  const refiners = ((suppliers.data?.data ?? []) as unknown as Supplier[]).filter((s) => s.is_active !== false);

  const gold = masters.metals.find((m) => m.code === 'GOLD');
  const [form, setForm] = useState({
    metalId: initialMetalId,
    branchId: initialBranchId,
    meltDate: today(),
    melterType: 'IN_HOUSE' as 'IN_HOUSE' | 'REFINER',
    melterName: '',
    refinerId: '',
    allowedLossPct: '1.50',
    expectedReturnDate: '',
    remarks: '',
  });
  // Due back a week after it goes to a refiner, two days in-house — unless typed.
  const returnBy = form.expectedReturnDate || plusDays(form.meltDate, USUAL_DAYS_OUT[form.melterType]);
  const metalId = form.metalId || gold?.id || '';
  // The metal's own usual loss, unless typed differently for this melt.
  const metalLoss = (masters.metals.find((m) => m.id === metalId) as { melt_loss_pct?: string | null } | undefined)
    ?.melt_loss_pct;
  const [lossTyped, setLossTyped] = useState(false);
  useEffect(() => {
    if (!lossTyped) setForm((f) => ({ ...f, allowedLossPct: metalLoss ? Number(metalLoss).toFixed(2) : '1.50' }));
  }, [metalLoss, lossTyped]);
  const branchId = form.branchId || defaultBranchId || '';
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<string[]>(initialItemIds);
  const [lots, setLots] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  const stock = useQuery({
    queryKey: ['meltable', branchId, metalId],
    queryFn: () =>
      api.get<{ items: MeltableItem[]; lots: MeltableLot[] }>(
        `/meltings/meltable?branchId=${branchId}&metalId=${metalId}`,
      ),
    enabled: !!branchId && !!metalId,
  });
  const allItems = stock.data?.items ?? [];
  const allLots = stock.data?.lots ?? [];
  const q = search.trim().toLowerCase();
  const visible = q
    ? allItems.filter((i) => `${i.item_code} ${i.product_name} ${i.purity_code}`.toLowerCase().includes(q))
    : allItems;

  const chosenItems = allItems.filter((i) => items.includes(i.id));
  const chosenLots = allLots.filter((l) => n(lots[l.id]) > 0);
  const lotWt = (l: MeltableLot): number =>
    n(l.remaining_fine_wt) > 0 ? (n(l.remaining_wt) * n(lots[l.id])) / n(l.remaining_fine_wt) : 0;
  const totals = {
    gross: chosenItems.reduce((a, i) => a + n(i.gross_wt), 0) + chosenLots.reduce((a, l) => a + lotWt(l), 0),
    fine: chosenItems.reduce((a, i) => a + n(i.fine_wt), 0) + chosenLots.reduce((a, l) => a + n(lots[l.id]), 0),
    cost:
      chosenItems.reduce((a, i) => a + n(i.acquisition_cost), 0) +
      chosenLots.reduce((a, l) => a + n(lots[l.id]) * n(l.cost_per_fine_gram), 0),
  };
  const overLot = chosenLots.find((l) => n(lots[l.id]) > n(l.remaining_fine_wt));

  const reset = (patch: Partial<typeof form>): void => {
    setForm({ ...form, ...patch });
    setItems([]);
    setLots({});
  };

  const issue = useMutation({
    mutationFn: () =>
      api.post<{ meltingId: string; docNo: string; inputFineWt: string }>(
        '/meltings/issue',
        {
          branchId,
          meltDate: form.meltDate,
          metalId,
          melterType: form.melterType,
          ...(form.melterType === 'REFINER' && form.refinerId ? { refinerId: form.refinerId } : {}),
          ...(form.melterType === 'IN_HOUSE' && form.melterName.trim() ? { melterName: form.melterName.trim() } : {}),
          ...(items.length > 0 ? { itemIds: items } : {}),
          ...(chosenLots.length > 0
            ? { lotIds: chosenLots.map((l) => ({ lotId: l.id, fineWt: n(lots[l.id]).toFixed(4) })) }
            : {}),
          ...(form.allowedLossPct ? { allowedLossPct: form.allowedLossPct } : {}),
          expectedReturnDate: returnBy,
          ...(form.remarks.trim() ? { remarks: form.remarks.trim() } : {}),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (r) => {
      toast(`${r.docNo} sent for melting`, `${fine(r.inputFineWt)} out of stock and into "metal at melting"`);
      void client.invalidateQueries({ queryKey: ['meltings'] });
      void client.invalidateQueries({ queryKey: ['meltable'] });
      void client.invalidateQueries({ queryKey: ['items'] });
      onIssued(r.meltingId);
    },
    onError: setError,
  });

  const canSend =
    !!branchId &&
    !!metalId &&
    (items.length > 0 || chosenLots.length > 0) &&
    !overLot &&
    (form.melterType === 'IN_HOUSE' || !!form.refinerId) &&
    returnBy >= form.meltDate &&
    !issue.isPending;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (canSend) issue.mutate();
  };

  return (
    <Drawer
      wide
      title="Send for melting"
      subtitle="The pieces and scrap chosen leave stock now and are held at cost in “metal at melting” until the bars come back."
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-ink-soft">Sending </span>
            <span className="num font-semibold">{fine(totals.fine.toFixed(4))}</span>
            <span className="text-ink-soft"> fine · </span>
            <span className="num">{weight(totals.gross.toFixed(3))}</span>
            <span className="text-ink-soft"> gross · </span>
            <span className="num">{money(totals.cost.toFixed(2))}</span>
            <span className="text-ink-soft"> at cost</span>
          </div>
          <button className="btn-primary" disabled={!canSend} onClick={() => issue.mutate()}>
            {issue.isPending ? 'Sending…' : 'Send for melting'}
          </button>
        </div>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Metal" required>
            <Select value={metalId} onChange={(v) => reset({ metalId: v })} options={masters.metalOptions} />
          </Field>
          {branches.length > 1 ? (
            <Field label="Branch" required>
              <Select
                value={branchId}
                onChange={(v) => reset({ branchId: v })}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
          ) : null}
          <Field label="Sent on" required>
            <DateInput value={form.meltDate} onChange={(v) => setForm({ ...form, meltDate: v })} />
          </Field>
          <Field label="Loss allowed" hint="Above this, a second person approves.">
            <PercentInput
              value={form.allowedLossPct}
              onChange={(v) => {
                setLossTyped(true);
                setForm({ ...form, allowedLossPct: v });
              }}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Melted by" required>
            <Select
              value={form.melterType}
              onChange={(v) => setForm({ ...form, melterType: (v || 'IN_HOUSE') as 'IN_HOUSE' | 'REFINER' })}
              options={[
                { value: 'IN_HOUSE', label: 'In-house / karigar' },
                { value: 'REFINER', label: 'Refiner (on the books)' },
              ]}
            />
          </Field>
          {form.melterType === 'REFINER' ? (
            <Field label="Refiner" required hint="Their bill is booked to their account.">
              <Select
                value={form.refinerId}
                onChange={(v) => setForm({ ...form, refinerId: v })}
                options={refiners
                  .sort((a, b) => Number(b.supplier_type === 'REFINER') - Number(a.supplier_type === 'REFINER'))
                  .map((s) => ({
                    value: s.id,
                    label: `${s.name}${s.supplier_type === 'REFINER' ? '' : ` (${(s.supplier_type ?? '').toLowerCase()})`}`,
                  }))}
              />
            </Field>
          ) : (
            <Field label="Karigar / melter name" hint="Who has the metal — for the loss-by-melter report.">
              <input
                className="field"
                value={form.melterName}
                onChange={(e) => setForm({ ...form, melterName: e.target.value })}
              />
            </Field>
          )}
          <Field
            label="Due back by"
            hint={form.expectedReturnDate ? undefined : `Usually ${USUAL_DAYS_OUT[form.melterType]} days.`}
          >
            <DateInput value={returnBy} onChange={(v) => setForm({ ...form, expectedReturnDate: v })} />
          </Field>
          <Field label="Remarks">
            <input
              className="field"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Field>
        </div>

        <section>
          <div className="mb-2 flex items-end justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Pieces</h3>
              <p className="text-2xs text-ink-soft">Tagged stock of this metal at this branch.</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                className="field w-56"
                placeholder="Find by tag, product, purity"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {visible.length > 0 ? (
                <button
                  type="button"
                  className="btn-quiet text-xs"
                  onClick={() => {
                    const ids = visible.map((i) => i.id);
                    const all = ids.every((id) => items.includes(id));
                    setItems(all ? items.filter((id) => !ids.includes(id)) : [...new Set([...items, ...ids])]);
                  }}
                >
                  {visible.every((i) => items.includes(i.id)) ? 'Clear' : 'Select all'}
                </button>
              ) : null}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto rounded border border-silver-100 bg-surface">
            {stock.isLoading ? (
              <p className="p-3 text-sm text-ink-soft">Loading stock…</p>
            ) : visible.length === 0 ? (
              <p className="p-3 text-sm text-ink-soft">No pieces of this metal in stock here.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-2xs text-ink-soft">
                    <th className="w-8 p-2" />
                    <th className="p-2 text-left">Tag</th>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-left">Purity</th>
                    <th className="p-2 text-right">Gross</th>
                    <th className="p-2 text-right">Fine</th>
                    <th className="p-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((i) => (
                    <tr
                      key={i.id}
                      className={`cursor-pointer border-t border-silver-100 ${items.includes(i.id) ? 'bg-gold-50' : 'hover:bg-gold-50/50'}`}
                      onClick={() => setItems((c) => (c.includes(i.id) ? c.filter((x) => x !== i.id) : [...c, i.id]))}
                    >
                      <td className="p-2 text-center">
                        <input type="checkbox" readOnly checked={items.includes(i.id)} />
                      </td>
                      <td className="num p-2">
                        {i.item_code}
                        {i.acquisition_doc_type === 'MELTING' ? (
                          <span className="ml-1 text-2xs text-ink-soft">bar</span>
                        ) : null}
                      </td>
                      <td className="p-2">{i.product_name}</td>
                      <td className="num p-2">{i.purity_code}</td>
                      <td className="num p-2 text-right">{weight(i.gross_wt)}</td>
                      <td className="num p-2 text-right">{fine(i.fine_wt)}</td>
                      <td className="num p-2 text-right">{money(i.acquisition_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold">Old gold and scrap lots</h3>
          <p className="mb-2 text-2xs text-ink-soft">Take all of a lot, or type how many fine grams to take from it.</p>
          {allLots.length === 0 ? (
            <p className="rounded border border-silver-100 bg-surface p-3 text-sm text-ink-soft">
              No open lots of this metal here.
            </p>
          ) : (
            <div className="rounded border border-silver-100 bg-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-2xs text-ink-soft">
                    <th className="p-2 text-left">Lot</th>
                    <th className="p-2 text-left">What</th>
                    <th className="p-2 text-left">Purity</th>
                    <th className="p-2 text-right">Holds (fine)</th>
                    <th className="p-2 text-right">Cost / fine g</th>
                    <th className="w-40 p-2 text-right">Take (fine g)</th>
                    <th className="w-16 p-2" />
                  </tr>
                </thead>
                <tbody>
                  {allLots.map((l) => {
                    const over = n(lots[l.id]) > n(l.remaining_fine_wt);
                    return (
                      <tr key={l.id} className={`border-t border-silver-100 ${n(lots[l.id]) > 0 ? 'bg-gold-50' : ''}`}>
                        <td className="num p-2">{l.lot_code}</td>
                        <td className="p-2">
                          {l.product_name}
                          <span className="ml-1 text-2xs text-ink-soft">
                            {l.source_doc_type === 'OLD_GOLD'
                              ? 'old gold'
                              : l.source_doc_type === 'MELTING_RECOVERY'
                                ? 'melt dust'
                                : ''}
                          </span>
                        </td>
                        <td className="num p-2">{l.purity_code}</td>
                        <td className="num p-2 text-right">{fine(l.remaining_fine_wt)}</td>
                        <td className="num p-2 text-right">{money(l.cost_per_fine_gram)}</td>
                        <td className="p-2">
                          <NumberInput
                            value={lots[l.id] ?? ''}
                            onChange={(v) => setLots({ ...lots, [l.id]: v })}
                            decimals={4}
                            suffix="g"
                          />
                          {over ? <span className="text-2xs text-danger">More than the lot holds</span> : null}
                        </td>
                        <td className="p-2 text-right">
                          <button
                            type="button"
                            className="btn-quiet px-2 text-xs"
                            onClick={() =>
                              setLots({ ...lots, [l.id]: n(lots[l.id]) > 0 ? '' : n(l.remaining_fine_wt).toFixed(4) })
                            }
                          >
                            {n(lots[l.id]) > 0 ? 'None' : 'All'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </form>
    </Drawer>
  );
}

/* ═══════════════════════════ bars back ═══════════════════════════════════ */

interface BarLine {
  barNo: string;
  weight: string;
  purityId: string;
  assayPct: string;
  hallmarkUid: string;
}

function ReceiveDrawer({ melt, onClose, onDone }: { melt: MeltDetail; onClose: () => void; onDone: () => void }) {
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const masters = useMasters();
  const { can } = usePermission();

  const purities = masters.puritiesFor(melt.metal_id);
  // The finest purity is what a melt usually comes back as.
  const finest = [...purities].sort((a, b) => n(b.fineness) - n(a.fineness))[0]?.id ?? '';
  const blankBar = (): BarLine => ({ barNo: '', weight: '', purityId: finest, assayPct: '', hallmarkUid: '' });

  const [bars, setBars] = useState<BarLine[]>([blankBar()]);
  useEffect(() => {
    // Purities load after the drawer opens on a cold cache.
    setBars((b) => b.map((x) => (x.purityId ? x : { ...x, purityId: finest })));
  }, [finest]);
  const [form, setForm] = useState({
    receivedDate: today(),
    sampleWt: '',
    recoveredWt: '',
    recoveredPurityId: '',
    charges: '',
    chargesTax: '',
    chargesPaidBy: (melt.refiner_id ? 'CREDIT' : 'CASH') as 'CREDIT' | 'CASH' | 'BANK',
    chargesInterstate: false,
    refinerBillNo: '',
    testingMethod: melt.refiner_id ? 'FIRE_ASSAY' : 'XRF',
    assayCertNo: '',
    assayedBy: '',
    chargesFineWt: '',
    excessLossTreatment: 'WRITE_OFF' as 'WRITE_OFF' | 'RECOVER',
    recoveryRate: '',
    remarks: '',
  });
  const [error, setError] = useState<unknown>(null);

  const fineness = (id: string): number => n(purities.find((p) => p.id === id)?.fineness);
  const barFine = (b: BarLine): number => Math.round(n(b.weight) * fineness(b.purityId) * 10000) / 10000;
  const outWt = bars.reduce((a, b) => a + n(b.weight), 0);
  const outFine = bars.reduce((a, b) => a + barFine(b), 0);
  const sampleFine = outWt > 0 ? Math.round(((n(form.sampleWt) * outFine) / outWt) * 10000) / 10000 : 0;
  const dustFine = Math.round(n(form.recoveredWt) * fineness(form.recoveredPurityId) * 10000) / 10000;
  const inFine = n(melt.input_fine_wt);
  // The refiner's fee taken in metal is accounted for — it is not loss.
  const feeFine = Math.round(n(form.chargesFineWt) * 10000) / 10000;
  const loss = Math.round((inFine - outFine - sampleFine - dustFine - feeFine) * 10000) / 10000;
  const lossPct = inFine > 0 ? (loss / inFine) * 100 : 0;
  const allowed = n(melt.allowed_loss_pct);
  const over = lossPct > allowed + 1e-9;
  const selfApprove = can('melting.approve') && can('melting.self_approve');
  const costPerFine = inFine > 0 ? n(melt.input_cost) / inFine : 0;
  const excessFine = Math.round(Math.max(0, loss - (inFine * allowed) / 100) * 10000) / 10000;
  const barsCost = n(melt.input_cost) + n(form.charges) - excessFine * costPerFine - dustFine * costPerFine;
  const recovering = form.excessLossTreatment === 'RECOVER' && !!melt.refiner_id && excessFine > 0;
  const recoveryRate = n(form.recoveryRate) > 0 ? n(form.recoveryRate) : costPerFine;
  const recoveryAmount = excessFine * recoveryRate;

  const ready =
    bars.length > 0 &&
    bars.every((b) => n(b.weight) > 0 && b.purityId) &&
    outFine > 0 &&
    loss >= 0 &&
    (n(form.recoveredWt) === 0 || !!form.recoveredPurityId);

  const receive = useMutation({
    mutationFn: () =>
      api.post<{ status: string; docNo: string; lossPct: string }>(`/meltings/${melt.id}/receive`, {
        receivedDate: form.receivedDate,
        bars: bars.map((b) => ({
          ...(b.barNo.trim() ? { barNo: b.barNo.trim() } : {}),
          weight: n(b.weight).toFixed(3),
          purityId: b.purityId,
          ...(b.assayPct ? { assayPct: b.assayPct } : {}),
          ...(b.hallmarkUid.trim() ? { hallmarkUid: b.hallmarkUid.trim() } : {}),
        })),
        ...(n(form.sampleWt) > 0 ? { sampleWt: n(form.sampleWt).toFixed(3) } : {}),
        ...(n(form.recoveredWt) > 0
          ? { recoveredWt: n(form.recoveredWt).toFixed(3), recoveredPurityId: form.recoveredPurityId }
          : {}),
        ...(n(form.charges) > 0 ? { charges: n(form.charges).toFixed(2) } : {}),
        ...(n(form.chargesTax) > 0 ? { chargesTax: n(form.chargesTax).toFixed(2) } : {}),
        ...(n(form.charges) + n(form.chargesTax) > 0 ? { chargesPaidBy: form.chargesPaidBy } : {}),
        ...(melt.refiner_id ? { chargesInterstate: form.chargesInterstate } : {}),
        ...(form.refinerBillNo.trim() ? { refinerBillNo: form.refinerBillNo.trim() } : {}),
        ...(form.testingMethod ? { testingMethod: form.testingMethod } : {}),
        ...(form.assayCertNo.trim() ? { assayCertNo: form.assayCertNo.trim() } : {}),
        ...(form.assayedBy.trim() ? { assayedBy: form.assayedBy.trim() } : {}),
        ...(feeFine > 0 ? { chargesFineWt: feeFine.toFixed(4) } : {}),
        ...(melt.refiner_id && excessFine > 0 ? { excessLossTreatment: form.excessLossTreatment } : {}),
        ...(recovering && n(form.recoveryRate) > 0 ? { recoveryRate: n(form.recoveryRate).toFixed(2) } : {}),
        ...(form.remarks.trim() ? { remarks: form.remarks.trim() } : {}),
      }),
    onSuccess: (r) => {
      toast(
        r.status === 'POSTED' ? `${r.docNo} completed` : `${r.docNo} held for approval`,
        r.status === 'POSTED'
          ? 'The bars are in stock, tagged and ready to sell.'
          : `Loss ${n(r.lossPct).toFixed(2)}% is above the allowance — a second person approves it.`,
      );
      void client.invalidateQueries({ queryKey: ['meltings'] });
      void client.invalidateQueries({ queryKey: ['melting', melt.id] });
      void client.invalidateQueries({ queryKey: ['melting-bars'] });
      onDone();
    },
    onError: setError,
  });

  const setBar = (i: number, patch: Partial<BarLine>): void =>
    setBars((c) => c.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const purityOptions = purities.map((p) => ({ value: p.id, label: `${p.code}${p.name ? ` — ${p.name}` : ''}` }));
  const purityCodes = purities.map((p) => ({ value: p.id, label: p.code }));

  return (
    <Drawer
      wide
      title={`Bars back — ${melt.doc_no}`}
      subtitle={`${fine(melt.input_fine_wt)} fine went to ${melt.refiner_name ?? melt.melter_name ?? 'in-house melting'} on ${date(melt.melt_date)}. Enter what came back.`}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className={`text-sm ${loss < 0 || over ? 'text-danger' : 'text-ink-soft'}`}>
            {loss < 0
              ? 'More metal back than went in — check the weights and purity.'
              : over
                ? selfApprove
                  ? `Loss ${pct(lossPct, allowed)} is over the ${allowed.toFixed(2)}% allowed. You may approve it yourself; the excess is ${recovering ? 'charged to the refiner' : 'written off'}.`
                  : `Loss ${pct(lossPct, allowed)} is over the ${allowed.toFixed(2)}% allowed. It will wait for approval.`
                : outFine > 0
                  ? `Loss ${lossPct.toFixed(2)}% — within the ${allowed.toFixed(2)}% allowed. Posts at once.`
                  : 'Enter the bar weights.'}
          </p>
          <button className="btn-primary" disabled={!ready || receive.isPending} onClick={() => receive.mutate()}>
            {receive.isPending
              ? 'Saving…'
              : over && !selfApprove
                ? 'Submit for approval'
                : over
                  ? 'Approve and post'
                  : 'Post'}
          </button>
        </div>
      }
    >
      <ErrorNotice error={error} />
      {melt.returned_note ? (
        <p className="mb-3 rounded border border-danger/30 bg-danger/5 p-2 text-sm">
          <span className="font-medium">Sent back:</span> {melt.returned_note}
        </p>
      ) : null}
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => e.preventDefault()} className="space-y-5">
        <section>
          <div className="mb-2 flex items-end justify-between">
            <div>
              <h3 className="text-sm font-semibold">Bars</h3>
              <p className="text-2xs text-ink-soft">
                Each bar becomes a tagged piece. Leave the number blank to use {melt.doc_no}.
              </p>
            </div>
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => setBars([...bars, blankBar()])}
            >
              + Another bar
            </button>
          </div>
          <div className="rounded border border-silver-100 bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-2xs text-ink-soft">
                  <th className="p-2 text-left">Bar no.</th>
                  <th className="p-2 text-right">Weight</th>
                  <th className="p-2 text-left">Purity</th>
                  <th className="p-2 text-right">Assay %</th>
                  <th className="p-2 text-left">HUID</th>
                  <th className="p-2 text-right">Fine</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {bars.map((b, i) => (
                  <tr key={i} className="border-t border-silver-100 align-top">
                    <td className="p-1.5">
                      <input
                        className="field num"
                        value={b.barNo}
                        placeholder={bars.length === 1 ? melt.doc_no : `${melt.doc_no}-${i + 1}`}
                        onChange={(e) => setBar(i, { barNo: e.target.value.toUpperCase() })}
                      />
                    </td>
                    <td className="w-28 p-1.5">
                      <NumberInput
                        value={b.weight}
                        onChange={(v) => setBar(i, { weight: v })}
                        decimals={3}
                        suffix="g"
                      />
                    </td>
                    <td className="w-24 p-1.5">
                      <Select value={b.purityId} onChange={(v) => setBar(i, { purityId: v })} options={purityCodes} />
                    </td>
                    <td className="w-20 p-1.5">
                      <NumberInput
                        value={b.assayPct}
                        onChange={(v) => setBar(i, { assayPct: v })}
                        decimals={2}
                        max={100}
                      />
                    </td>
                    <td className="w-28 p-1.5">
                      <input
                        className="field num"
                        value={b.hallmarkUid}
                        onChange={(e) => setBar(i, { hallmarkUid: e.target.value.toUpperCase() })}
                      />
                    </td>
                    <td className="num whitespace-nowrap p-2 text-right">{fine(barFine(b).toFixed(4))}</td>
                    <td className="p-1.5 text-right">
                      {bars.length > 1 ? (
                        <button
                          type="button"
                          className="btn-quiet px-2 text-xs"
                          onClick={() => setBars(bars.filter((_, j) => j !== i))}
                        >
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
          <div className="space-y-5">
            <section className="grid gap-3 sm:grid-cols-4">
              <Field label="Received on" required>
                <DateInput
                  value={form.receivedDate}
                  min={melt.melt_date}
                  onChange={(v) => setForm({ ...form, receivedDate: v })}
                />
              </Field>
              <Field label="Assay sample" hint="Cut from the bars for testing.">
                <NumberInput
                  value={form.sampleWt}
                  onChange={(v) => setForm({ ...form, sampleWt: v })}
                  decimals={3}
                  suffix="g"
                />
              </Field>
              <Field label="Dust recovered" hint="Kept as scrap.">
                <NumberInput
                  value={form.recoveredWt}
                  onChange={(v) => setForm({ ...form, recoveredWt: v })}
                  decimals={3}
                  suffix="g"
                />
              </Field>
              <Field label="Dust purity" required={n(form.recoveredWt) > 0}>
                <Select
                  value={form.recoveredPurityId}
                  onChange={(v) => setForm({ ...form, recoveredPurityId: v })}
                  options={purityOptions}
                />
              </Field>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Assay</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Tested by">
                  <Select
                    value={form.testingMethod}
                    onChange={(v) => setForm({ ...form, testingMethod: v })}
                    options={TESTING}
                  />
                </Field>
                <Field label="Certificate no.">
                  <input
                    className="field num"
                    value={form.assayCertNo}
                    onChange={(e) => setForm({ ...form, assayCertNo: e.target.value })}
                  />
                </Field>
                <Field label="Assayed by">
                  <input
                    className="field"
                    value={form.assayedBy}
                    onChange={(e) => setForm({ ...form, assayedBy: e.target.value })}
                  />
                </Field>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Melting / refining charges</h3>
              <div className="grid gap-3 sm:grid-cols-4">
                <Field label="Charges" hint="Added to the bars’ cost.">
                  <NumberInput
                    value={form.charges}
                    onChange={(v) => setForm({ ...form, charges: v })}
                    decimals={2}
                    suffix="₹"
                  />
                </Field>
                <Field
                  label="GST on the bill"
                  hint={
                    melt.refiner_id
                      ? melt.refiner_registered
                        ? 'Claimed as input tax.'
                        : 'Refiner is not GST-registered.'
                      : 'Only on a registered refiner’s bill.'
                  }
                >
                  <NumberInput
                    value={form.chargesTax}
                    onChange={(v) => setForm({ ...form, chargesTax: v })}
                    decimals={2}
                    suffix="₹"
                    disabled={!melt.refiner_id || melt.refiner_registered === false}
                  />
                </Field>
                <Field label="Paid by">
                  <Select
                    value={form.chargesPaidBy}
                    onChange={(v) => setForm({ ...form, chargesPaidBy: (v || 'CASH') as 'CREDIT' | 'CASH' | 'BANK' })}
                    options={[
                      ...(melt.refiner_id ? [{ value: 'CREDIT', label: 'On the refiner’s account' }] : []),
                      { value: 'CASH', label: 'Cash' },
                      { value: 'BANK', label: 'Bank' },
                    ]}
                  />
                </Field>
                <Field label="Bill no.">
                  <input
                    className="field num"
                    value={form.refinerBillNo}
                    onChange={(e) => setForm({ ...form, refinerBillNo: e.target.value })}
                  />
                </Field>
                <Field label="Fee kept in metal" hint="Fine grams the refiner kept as the fee — not counted as loss.">
                  <NumberInput
                    value={form.chargesFineWt}
                    onChange={(v) => setForm({ ...form, chargesFineWt: v })}
                    decimals={4}
                    suffix="g"
                  />
                </Field>
              </div>
              {melt.refiner_id && n(form.chargesTax) > 0 ? (
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.chargesInterstate}
                    onChange={(e) => setForm({ ...form, chargesInterstate: e.target.checked })}
                  />
                  Refiner is in another state (IGST)
                </label>
              ) : null}
            </section>

            {excessFine > 0 ? (
              <section className="rounded-md border border-danger/30 bg-danger/5 p-3">
                <h3 className="text-sm font-semibold">Loss above the allowance: {fine(excessFine.toFixed(4))}</h3>
                <p className="mb-2 text-2xs text-ink-soft">
                  Worth {money((excessFine * costPerFine).toFixed(2))} at what the metal cost. Who bears it?
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="The excess is">
                    <Select
                      value={form.excessLossTreatment}
                      onChange={(v) =>
                        setForm({ ...form, excessLossTreatment: (v || 'WRITE_OFF') as 'WRITE_OFF' | 'RECOVER' })
                      }
                      options={[
                        { value: 'WRITE_OFF', label: 'Written off by the shop' },
                        ...(melt.refiner_id ? [{ value: 'RECOVER', label: 'Charged to the refiner’s account' }] : []),
                      ]}
                    />
                  </Field>
                  {recovering ? (
                    <Field
                      label="Charged at, per fine gram"
                      hint={`Blank charges what it cost: ${money(costPerFine.toFixed(2))}.`}
                    >
                      <NumberInput
                        value={form.recoveryRate}
                        onChange={(v) => setForm({ ...form, recoveryRate: v })}
                        decimals={2}
                        suffix="₹"
                      />
                    </Field>
                  ) : !melt.refiner_id ? (
                    <p className="self-end text-2xs text-ink-soft">
                      Only a refiner on the books can be charged for a loss. Choose the refiner when sending, to recover
                      it.
                    </p>
                  ) : null}
                </div>
              </section>
            ) : null}

            <Field label="Remarks">
              <input
                className="field"
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </Field>
          </div>

          <aside
            className={`h-fit rounded-md border p-3 text-sm ${loss < 0 || over ? 'border-danger/40 bg-danger/5' : 'border-silver-100 bg-surface'}`}
          >
            <h3 className="mb-2 text-sm font-semibold">Where every gram went</h3>
            <Row label="Sent (fine)">{fine(inFine.toFixed(4))}</Row>
            <Row label={`In ${bars.length} bar${bars.length === 1 ? '' : 's'}`}>{fine(outFine.toFixed(4))}</Row>
            <Row label="Assay sample">{fine(sampleFine.toFixed(4))}</Row>
            <Row label="Dust kept">{fine(dustFine.toFixed(4))}</Row>
            {feeFine > 0 ? <Row label="Fee kept in metal">{fine(feeFine.toFixed(4))}</Row> : null}
            <Row label="Lost" strong tone={loss < 0 || over ? 'text-danger' : ''}>
              {fine(loss.toFixed(4))}
            </Row>
            <Row label="Loss %" tone={over ? 'text-danger font-semibold' : ''}>
              {pct(lossPct, allowed)} <span className="text-ink-faint">/ {allowed.toFixed(2)}%</span>
            </Row>
            <div className="my-2 border-t border-ink/10" />
            <h3 className="mb-1 text-sm font-semibold">What the bars cost</h3>
            <Row label="Metal at cost">{money(n(melt.input_cost).toFixed(2))}</Row>
            <Row label="+ Charges">{money(n(form.charges).toFixed(2))}</Row>
            {dustFine > 0 ? <Row label="− Dust to scrap">{money((dustFine * costPerFine).toFixed(2))}</Row> : null}
            {excessFine > 0 ? (
              <Row
                label={recovering ? '− Excess loss, to the refiner' : '− Excess loss written off'}
                tone="text-danger"
              >
                {money((excessFine * costPerFine).toFixed(2))}
              </Row>
            ) : null}
            <Row label="Bars" strong>
              {money(barsCost.toFixed(2))}
            </Row>
            {outFine > 0 ? <Row label="Per fine gram">{money((barsCost / outFine).toFixed(2))}</Row> : null}
            {recovering ? (
              <>
                <div className="my-2 border-t border-ink/10" />
                <Row label="Refiner is charged" strong>
                  {money(recoveryAmount.toFixed(2))}
                </Row>
                <p className="text-2xs text-ink-soft">
                  {fine(excessFine.toFixed(4))} × {money(recoveryRate.toFixed(2))}, taken off what the shop owes them.
                </p>
              </>
            ) : null}
          </aside>
        </div>
      </form>
    </Drawer>
  );
}

/* ═══════════════════════════ one melt ════════════════════════════════════ */

function DetailDrawer({
  id,
  onClose,
  onReceive,
}: {
  id: string;
  onClose: () => void;
  onReceive: (m: MeltDetail) => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = usePermission();
  const me = useAuth((s) => s.session?.user.id);
  const [dialog, setDialog] = useState<'cancel' | 'reopen' | 'sendBack' | 'approve' | 'returnBy' | null>(null);
  const [error, setError] = useState<unknown>(null);

  const q = useQuery({ queryKey: ['melting', id], queryFn: () => api.get<MeltDetail>(`/meltings/${id}`) });
  const m = q.data;

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body: Record<string, unknown> }) =>
      api.post<{ status: string; docNo: string }>(`/meltings/${id}/${path}`, body),
    onSuccess: (r) => {
      toast(r.docNo ? `${r.docNo}: ${STATUS[r.status]?.label.toLowerCase() ?? r.status}` : 'Due date changed');
      setDialog(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['meltings'] });
      void client.invalidateQueries({ queryKey: ['melting', id] });
      void client.invalidateQueries({ queryKey: ['melting-bars'] });
      void client.invalidateQueries({ queryKey: ['meltable'] });
    },
    onError: (e) => {
      setDialog(null);
      setError(e);
    },
  });

  const journals = useMemo(() => {
    const byNo = new Map<
      string,
      { no: string; date: string; narration: string | null; status: string; lines: MeltDetail['journals'] }
    >();
    for (const l of m?.journals ?? []) {
      const j = byNo.get(l.journal_no) ?? {
        no: l.journal_no,
        date: l.journal_date,
        narration: l.narration,
        status: l.status,
        lines: [],
      };
      j.lines.push(l);
      byNo.set(l.journal_no, j);
    }
    return [...byNo.values()];
  }, [m]);

  if (!m) {
    return (
      <Drawer wide title="Melt" onClose={onClose}>
        {q.isLoading ? <Loading /> : <ErrorNotice error={q.error} />}
      </Drawer>
    );
  }

  const isOut = m.status === 'IN_PROCESS';
  const isPending = m.status === 'PENDING_APPROVAL';
  const isPosted = m.status === 'POSTED';
  const receivedBySelf = m.received_by === me;
  const canApprove = isPending && can('melting.approve') && (!receivedBySelf || can('melting.self_approve'));
  const canReopen =
    isPosted && can('document.void') && !!m.issued_at && m.bars.every((b) => b.item_status === 'IN_STOCK');
  const melter = m.refiner_name ?? m.melter_name ?? 'In-house';

  const actions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {(isOut || isPending) && can('document.void') ? (
        <button className="btn-quiet text-danger" onClick={() => setDialog('cancel')}>
          Cancel melt
        </button>
      ) : null}
      {canReopen ? (
        <button className="btn-quiet text-danger" onClick={() => setDialog('reopen')}>
          Reopen
        </button>
      ) : null}
      {isOut && can('melting.create') ? (
        <button className="btn-ghost" onClick={() => setDialog('returnBy')}>
          Change due date
        </button>
      ) : null}
      <button className="btn-ghost" onClick={() => navigate(`/melting/${m.id}/print`)}>
        Print slip
      </button>
      {isPosted && m.bars.length > 0 ? (
        <button className="btn-ghost" onClick={() => navigate(`/melting/${m.id}/tags`)}>
          Print bar tags
        </button>
      ) : null}
      {isPending && can('melting.approve') ? (
        <button className="btn-ghost" onClick={() => setDialog('sendBack')}>
          Send back
        </button>
      ) : null}
      {canApprove ? (
        <button className="btn-primary" onClick={() => setDialog('approve')}>
          Approve loss
        </button>
      ) : null}
      {isOut && can('melting.create') ? (
        <button className="btn-primary" onClick={() => onReceive(m)}>
          Receive bars
        </button>
      ) : null}
    </div>
  );

  return (
    <Drawer
      wide
      title={`${m.doc_no} — ${m.metal_name}`}
      subtitle={`Sent ${date(m.melt_date)} to ${melter}${m.received_date ? ` · back ${date(m.received_date)}` : ''}`}
      onClose={onClose}
      footer={actions}
    >
      <ErrorNotice error={error} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusChip status={m.status} />
        {isPending && receivedBySelf && !can('melting.self_approve') ? (
          <span className="text-2xs text-ink-soft">You received this melt, so someone else approves its loss.</span>
        ) : null}
        {m.returned_note && isOut ? <span className="text-2xs text-danger">{m.returned_note}</span> : null}
        {m.cancel_reason ? <span className="text-2xs text-ink-soft">Cancelled: {m.cancel_reason}</span> : null}
        {isOut && m.expected_return_date ? (
          m.days_overdue > 0 ? (
            <Chip tone="bad">
              {m.days_overdue} day{m.days_overdue === 1 ? '' : 's'} late — was due {date(m.expected_return_date)}
            </Chip>
          ) : (
            <span className="text-2xs text-ink-soft">Due back by {date(m.expected_return_date)}</span>
          )
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-md border border-silver-100 bg-surface p-3 text-sm">
          <h3 className="mb-1 font-semibold">Metal</h3>
          <Row label="Sent (fine)">{fine(m.input_fine_wt)}</Row>
          <Row label="Bars (fine)">{m.output_fine_wt ? fine(m.output_fine_wt) : '—'}</Row>
          <Row label="Assay sample">{fine(m.sample_fine_wt)}</Row>
          <Row label={`Dust kept${m.recovered_lot_code ? ` (${m.recovered_lot_code})` : ''}`}>
            {fine(m.recovered_fine_wt)}
          </Row>
          {n(m.charges_fine_wt) > 0 ? <Row label="Fee kept in metal">{fine(m.charges_fine_wt)}</Row> : null}
          <Row label="Lost" strong>
            {m.loss_fine_wt ? fine(m.loss_fine_wt) : '—'}
          </Row>
          <Row label="Loss %" tone={n(m.loss_pct) > n(m.allowed_loss_pct) ? 'text-danger font-semibold' : ''}>
            {m.loss_pct ? pct(n(m.loss_pct), n(m.allowed_loss_pct)) : '—'}{' '}
            <span className="text-ink-faint">/ {n(m.allowed_loss_pct).toFixed(2)}%</span>
          </Row>
        </div>
        <div className="rounded-md border border-silver-100 bg-surface p-3 text-sm">
          <h3 className="mb-1 font-semibold">Money</h3>
          <Row label="Metal at cost">{money(m.input_cost)}</Row>
          <Row label="Charges">{money(m.charges)}</Row>
          {n(m.charges_tax) > 0 ? (
            <Row label={m.charges_interstate ? 'IGST' : 'CGST + SGST'}>{money(m.charges_tax)}</Row>
          ) : null}
          {n(m.excess_loss_value) > 0 ? (
            <Row
              label={n(m.recovered_amount) > 0 ? 'Excess loss, at cost' : 'Excess loss written off'}
              tone="text-danger"
            >
              {money(m.excess_loss_value)}
            </Row>
          ) : null}
          {n(m.recovered_amount) > 0 ? (
            <Row label={`Charged back${m.recovery_rate ? ` @ ${money(m.recovery_rate)}/g` : ''}`} tone="text-success">
              {money(m.recovered_amount)}
            </Row>
          ) : null}
          <Row label="Bars cost" strong>
            {isPosted ? money(m.bars.reduce((a, b) => a + n(b.cost_amount), 0).toFixed(2)) : '—'}
          </Row>
          {m.charges_paid_by ? (
            <Row label="Paid by">
              {m.charges_paid_by === 'CREDIT' ? 'Refiner’s account' : m.charges_paid_by.toLowerCase()}
            </Row>
          ) : null}
          {m.refiner_bill_no ? <Row label="Bill no.">{m.refiner_bill_no}</Row> : null}
        </div>
        <div className="rounded-md border border-silver-100 bg-surface p-3 text-sm">
          <h3 className="mb-1 font-semibold">Who and how</h3>
          <Row label="Sent by">{m.requested_by_name ?? '—'}</Row>
          <Row label="Received by">{m.received_by_name ?? '—'}</Row>
          <Row label="Loss approved by">{m.approved_by_name ?? (isPosted ? 'within allowance' : '—')}</Row>
          <Row label={isOut ? 'Due back by' : 'Was due by'}>
            {m.expected_return_date ? date(m.expected_return_date) : '—'}
          </Row>
          <Row label="Tested by">
            {m.testing_method ? testingWords(m.testing_method).replace(/^./, (c) => c.toUpperCase()) : '—'}
          </Row>
          <Row label="Assay cert">{m.assay_cert_no ?? '—'}</Row>
          {m.assayed_by ? <Row label="Assayed by">{m.assayed_by}</Row> : null}
          {m.remarks ? <p className="mt-1 text-2xs text-ink-soft">{m.remarks}</p> : null}
        </div>
      </div>

      {m.bars.length > 0 ? (
        <section className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">Bars</h3>
          <Table head={['Bar', 'Purity', 'Assay %', 'HUID', 'Weight', 'Fine', 'Cost', 'Cost / fine g', 'Status', '']}>
            {m.bars.map((b) => (
              <tr key={b.line_no}>
                <td className="td num font-medium">{b.bar_no}</td>
                <td className="td num">{b.purity_code}</td>
                <td className="td num text-right">{b.assay_pct ? n(b.assay_pct).toFixed(2) : '—'}</td>
                <td className="td num">{b.hallmark_uid ?? '—'}</td>
                <td className="td num text-right">{weight(b.weight)}</td>
                <td className="td num text-right">{fine(b.fine_wt)}</td>
                <td className="td num text-right">{b.cost_amount ? money(b.cost_amount) : '—'}</td>
                <td className="td num text-right">
                  {b.cost_amount && n(b.fine_wt) > 0 ? money((n(b.cost_amount) / n(b.fine_wt)).toFixed(2)) : '—'}
                </td>
                <td className="td text-xs">
                  {b.sold_doc_no ? (
                    <Link className="text-gold-700 hover:underline" to={`/sales/${b.sold_doc_id}/print`}>
                      sold on {b.sold_doc_no}
                    </Link>
                  ) : (
                    (b.item_status ?? (isPosted ? '' : 'not yet in stock')).replace(/_/g, ' ').toLowerCase()
                  )}
                </td>
                <td className="td text-right">
                  {b.item_status === 'IN_STOCK' && can('sales.create') ? (
                    <button
                      className="btn-ghost px-2 py-1 text-xs"
                      onClick={() => navigate(`/pos?item=${encodeURIComponent(b.bar_no)}`)}
                    >
                      Sell
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        </section>
      ) : null}

      <section className="mt-5">
        <h3 className="mb-2 text-sm font-semibold">What went in</h3>
        <Table head={['#', 'Tag / lot', 'What', 'Purity', 'Gross', 'Fine', 'Cost']}>
          {m.lines.map((l) => (
            <tr key={l.line_no}>
              <td className="td num">{l.line_no}</td>
              <td className="td num">{l.item_code ?? l.lot_code ?? l.description}</td>
              <td className="td">{l.product_name ?? (l.lot_code ? 'Scrap lot' : '—')}</td>
              <td className="td num">{l.purity_code}</td>
              <td className="td num text-right">{weight(l.gross_wt)}</td>
              <td className="td num text-right">{fine(l.fine_wt)}</td>
              <td className="td num text-right">{money(l.cost_amount)}</td>
            </tr>
          ))}
        </Table>
      </section>

      {journals.length > 0 ? (
        <section className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">In the books</h3>
          <div className="space-y-3">
            {journals.map((j) => (
              <div key={j.no} className="rounded-md border border-silver-100 bg-surface p-3 text-sm">
                <div className="mb-1 flex justify-between text-2xs text-ink-soft">
                  <span>
                    <span className="num font-medium text-ink">{j.no}</span> · {date(j.date)} · {j.narration}
                  </span>
                  {j.status === 'REVERSED' ? <Chip tone="mute">reversed</Chip> : null}
                </div>
                <table className="w-full">
                  <tbody>
                    {j.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="py-0.5">
                          <span className="num text-ink-soft">{l.account_code}</span> {l.account_name}
                        </td>
                        <td className="num w-32 py-0.5 text-right">{n(l.debit) > 0 ? money(l.debit) : ''}</td>
                        <td className="num w-32 py-0.5 text-right">{n(l.credit) > 0 ? money(l.credit) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {m.trail.length > 0 ? (
        <section className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">History</h3>
          <ol className="space-y-1 text-sm">
            {m.trail.map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className="num w-36 shrink-0 text-2xs text-ink-soft">
                  {new Date(t.occurred_at).toLocaleString()}
                </span>
                <span>
                  <span className="font-medium">{ACTION_LABEL[t.action] ?? t.action}</span>
                  {t.user_name ? <span className="text-ink-soft"> — {t.user_name}</span> : null}
                  {t.reason ? <span className="block text-2xs text-ink-soft">{t.reason}</span> : null}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <ReasonDialog
        open={dialog === 'cancel'}
        tone="danger"
        title={`Cancel ${m.doc_no}?`}
        message="The pieces go back on the shelf, scrap back into its lots, and the entry that moved them to “metal at melting” is reversed. The melt stays on record, marked cancelled."
        label="Reason (kept on the record)"
        placeholder="Wrong pieces sent"
        confirmLabel="Cancel the melt"
        busy={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => act.mutate({ path: 'cancel', body: { reason } })}
      />
      <ReasonDialog
        open={dialog === 'reopen'}
        tone="danger"
        title={`Reopen ${m.doc_no}?`}
        message="Its bars come off the shelf, the completion entry is reversed, and the melt goes back to “out for melting” so the bars can be entered again."
        label="Reason (kept on the record)"
        placeholder="Bar weight entered wrongly"
        confirmLabel="Reopen"
        busy={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => act.mutate({ path: 'reopen', body: { reason } })}
      />
      <ReasonDialog
        open={dialog === 'sendBack'}
        title={`Send ${m.doc_no} back?`}
        message="The bars entered are cleared and the melt returns to “out for melting” to be entered again."
        label="What should be checked"
        placeholder="Weigh the bar again"
        confirmLabel="Send back"
        busy={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(note) => act.mutate({ path: 'send-back', body: { note } })}
      />
      <ApproveDialog
        open={dialog === 'approve'}
        melt={m}
        busy={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(body) => act.mutate({ path: 'approve', body })}
      />
      <ReturnByDialog
        open={dialog === 'returnBy'}
        melt={m}
        busy={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(body) => act.mutate({ path: 'return-by', body })}
      />
    </Drawer>
  );
}

/** Approving a high loss — and deciding who bears the part above the allowance. */
function ApproveDialog({
  open,
  melt,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  melt: MeltDetail;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (body: Record<string, unknown>) => void;
}) {
  const [note, setNote] = useState('');
  const [treatment, setTreatment] = useState<'WRITE_OFF' | 'RECOVER'>(melt.excess_loss_treatment ?? 'WRITE_OFF');
  const [rate, setRate] = useState(melt.recovery_rate ?? '');
  useEffect(() => {
    if (open) {
      setNote('');
      setTreatment(melt.excess_loss_treatment ?? 'WRITE_OFF');
      setRate(melt.recovery_rate ?? '');
    }
  }, [open, melt.excess_loss_treatment, melt.recovery_rate]);

  const inFine = n(melt.input_fine_wt);
  const costPerFine = inFine > 0 ? n(melt.input_cost) / inFine : 0;
  const excessFine = Math.max(0, n(melt.loss_fine_wt) - (inFine * n(melt.allowed_loss_pct)) / 100);
  const charge = excessFine * (n(rate) > 0 ? n(rate) : costPerFine);

  return (
    <ConfirmDialog
      open={open}
      title={`Approve the loss on ${melt.doc_no}?`}
      message={`${fine(melt.loss_fine_wt)} lost (${pct(n(melt.loss_pct), n(melt.allowed_loss_pct))} against ${n(melt.allowed_loss_pct).toFixed(2)}% allowed). ${fine(excessFine.toFixed(4))} is above the allowance, worth ${money((excessFine * costPerFine).toFixed(2))} at cost. The bars go into stock.`}
      detail={
        <div className="space-y-3 text-sm">
          {melt.refiner_id ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="The excess is">
                <Select
                  value={treatment}
                  onChange={(v) => setTreatment((v || 'WRITE_OFF') as 'WRITE_OFF' | 'RECOVER')}
                  options={[
                    { value: 'WRITE_OFF', label: 'Written off by the shop' },
                    { value: 'RECOVER', label: `Charged to ${melt.refiner_name ?? 'the refiner'}` },
                  ]}
                />
              </Field>
              {treatment === 'RECOVER' ? (
                <Field label="Per fine gram" hint={`Blank: at cost, ${money(costPerFine.toFixed(2))}.`}>
                  <NumberInput value={rate} onChange={setRate} decimals={2} suffix="₹" />
                </Field>
              ) : null}
            </div>
          ) : (
            <p className="text-2xs text-ink-soft">Melted in-house, so the excess is written off to melting loss.</p>
          )}
          {treatment === 'RECOVER' && melt.refiner_id ? (
            <p className="rounded bg-success/10 px-2 py-1 text-2xs">
              {money(charge.toFixed(2))} comes off what the shop owes {melt.refiner_name ?? 'the refiner'}.
            </p>
          ) : null}
          <label className="block">
            <span className="mb-1 block text-2xs text-ink-soft">Note (optional)</span>
            <input
              className="field w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Checked with the karigar"
            />
          </label>
        </div>
      }
      confirmLabel="Approve and post"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() =>
        onConfirm({
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(melt.refiner_id ? { excessLossTreatment: treatment } : {}),
          ...(melt.refiner_id && treatment === 'RECOVER' && n(rate) > 0 ? { recoveryRate: n(rate).toFixed(2) } : {}),
        })
      }
    />
  );
}

/** The melter needs more time: a new due date, and why. */
function ReturnByDialog({
  open,
  melt,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  melt: MeltDetail;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (body: Record<string, unknown>) => void;
}) {
  const [day, setDay] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setDay(melt.expected_return_date ? String(melt.expected_return_date).slice(0, 10) : today());
      setReason('');
    }
  }, [open, melt.expected_return_date]);
  return (
    <ConfirmDialog
      open={open}
      title={`New due date for ${melt.doc_no}`}
      message="The old date and the reason are kept in the melt’s history."
      detail={
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="Due back by" required>
            <DateInput value={day} min={String(melt.melt_date).slice(0, 10)} onChange={setDay} />
          </Field>
          <label className="block">
            <span className="mb-1 block text-2xs text-ink-soft">Reason</span>
            <input
              className="field w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Refiner closed for Diwali"
            />
          </label>
        </div>
      }
      confirmLabel="Save"
      confirmDisabled={!day || reason.trim().length < 3}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm({ expectedReturnDate: day, reason: reason.trim() })}
    />
  );
}
